import {
  ConsultationFormStatus,
  ExternalBookingPaymentStatus,
  ExternalBookingStatus,
} from '../../graphQL/generated/enums'

/** Icon keys both apps' icon sets are expected to carry for a booking's status. */
export type HeaderIcon = 'smTick' | 'smDoubleTick' | 'smDoubleChevron' | 'smCross' | 'smClock' | 'smPlus'

export type HeaderColor = 'primary' | 'success' | 'error' | 'darkGray'

/** Takes a string, not the enum: marketplace bookings carry statuses the GraphQL enum doesn't have. */
export const getBookingColors = (status: string): { color: HeaderColor, headerIcon: HeaderIcon } => {
  switch (status) {
    case ExternalBookingStatus.Completed:
      return { color: 'primary', headerIcon: 'smDoubleTick' }
    case ExternalBookingStatus.Cancelled:
    case ExternalBookingStatus.Declined:
    case 'NO_SHOW':
      return { color: 'error', headerIcon: 'smCross' }
    case ExternalBookingStatus.CancelRequested:
      return { color: 'error', headerIcon: 'smCross' }
    case ExternalBookingStatus.ToComplete:
      return { color: 'success', headerIcon: 'smDoubleChevron' }
    case ExternalBookingStatus.Requested:
    case 'APPROVAL_PENDING':
      return { color: 'primary', headerIcon: 'smPlus' }
    case ExternalBookingStatus.Draft:
      return { color: 'darkGray', headerIcon: 'smClock' }
    default:
      return { color: 'success', headerIcon: 'smTick' }
  }
}

export type BookingStatusBadgeKind =
  | 'newBooking'
  | 'needsCheckout'
  | 'readyToCheckout'
  | 'formDue'
  | 'depositDue'
  | 'cancelRequested'
  | 'rescheduleRequested'
  | 'dueNow'
  | 'fullyPaid'

export type BookingStatusBadgeInput = {
  bookingType: string | null | undefined
  bookingStatus?: string | null
  paymentStatus?: string | null
  consultationFormStatus?: string | null
  bookingStart?: Date | string | null
  rescheduleRequested?: boolean
}

const hasStarted = (bookingStart: Date | string | null | undefined) => {
  if (!bookingStart) return true
  const start = bookingStart instanceof Date ? bookingStart.getTime() : new Date(bookingStart).getTime()
  return Number.isNaN(start) || start <= Date.now()
}

/**
 * Colour says what kind of attention the booking needs, so the footer bar, the card and the
 * calendar corner badge all agree:
 *  - primary (blue)   the client is waiting on a decision from you
 *  - warning (orange) money is outstanding, or you're waiting on the client
 *  - success (green)  ready to move on: checkout
 *  - error (red)      destructive: the client wants to cancel
 */
export type BookingStatusBadgeColor = 'primary' | 'success' | 'warning' | 'error'

export const bookingStatusBadgeColors: Record<BookingStatusBadgeKind, BookingStatusBadgeColor> = {
  newBooking: 'primary',
  rescheduleRequested: 'error',
  needsCheckout: 'warning',
  readyToCheckout: 'success',
  formDue: 'warning',
  depositDue: 'warning',
  cancelRequested: 'error',
  dueNow: 'warning',
  fullyPaid: 'success',
}

export const bookingStatusBadgeLabels: Record<BookingStatusBadgeKind, string> = {
  newBooking: 'New booking request',
  needsCheckout: 'Checkout • Due',
  readyToCheckout: 'Checkout • Paid',
  formDue: 'Consultation form due',
  depositDue: 'Deposit due',
  cancelRequested: 'Client cancellation',
  rescheduleRequested: 'Reschedule request',
  fullyPaid: 'Fully paid',
  dueNow: '$0 due',
}

export const bookingStatusBadgeAmountLabels: Partial<Record<BookingStatusBadgeKind, string>> = {
  depositDue: '$0 deposit due',
  needsCheckout: 'Checkout • $0 due',
  dueNow: '$0 due',
}

export const bookingStatusBadgeIcon = (kind: BookingStatusBadgeKind) => {
  switch (kind) {
    case 'newBooking':
      return 'smPlus'
    case 'needsCheckout':
    case 'readyToCheckout':
      return 'smDoubleChevron'
    case 'formDue':
      return 'consultationForm'
    case 'depositDue':
    case 'dueNow':
      return 'smPound'
    case 'fullyPaid':
      return 'smDoubleTick'
    case 'cancelRequested':
      return 'smCross'
    case 'rescheduleRequested':
      return 'reschedule'
  }
}

const closedStatuses: string[] = [
  ExternalBookingStatus.Cancelled,
  ExternalBookingStatus.Declined,
]

const statusesWithoutPayments: string[] = [
  ...closedStatuses,
  ExternalBookingStatus.Requested,
]

/** A booking that's been checked out is done with: nothing is left to chase on it. */
const unbadgedStatuses: string[] = [
  ...closedStatuses,
  ExternalBookingStatus.Completed,
]

export const bookingAcceptsPayments = (bookingStatus?: string | null) => !bookingStatus || !statusesWithoutPayments.includes(bookingStatus)

const formDueStatuses: string[] = [
  ConsultationFormStatus.Requested,
  ConsultationFormStatus.ReminderSent,
]

/** Ordered most to least urgent: the first match is the booking's badge. */
export const bookingStatusBadge = (input: BookingStatusBadgeInput): BookingStatusBadgeKind | null => {
  const { bookingType, bookingStatus, paymentStatus, consultationFormStatus, bookingStart, rescheduleRequested } = input
  if (bookingType && bookingType !== 'external') return null
  if (bookingStatus && unbadgedStatuses.includes(bookingStatus)) return null

  const started = hasStarted(bookingStart)
  const acceptsPayments = bookingAcceptsPayments(bookingStatus)

  if (bookingStatus === ExternalBookingStatus.CancelRequested) return 'cancelRequested'
  if (bookingStatus === ExternalBookingStatus.Requested) return 'newBooking'
  if (rescheduleRequested) return 'rescheduleRequested'
  if (bookingStatus === ExternalBookingStatus.ToComplete || (bookingStatus === ExternalBookingStatus.Accepted && started)) {
    return paymentStatus === ExternalBookingPaymentStatus.FullyPaid ? 'readyToCheckout' : 'needsCheckout'
  }
  if (acceptsPayments && paymentStatus === ExternalBookingPaymentStatus.DepositNotPaid) return 'depositDue'
  if (consultationFormStatus && formDueStatuses.includes(consultationFormStatus)) return 'formDue'

  return null
}

export const bookingCtaLabel = (
  kind: BookingStatusBadgeKind,
  s: (text: string, ...args: (string | number)[]) => string,
  amount?: string,
) => {
  const amountLabel = bookingStatusBadgeAmountLabels[kind]
  if (amount && amountLabel) return s(amountLabel, amount)

  return s(bookingStatusBadgeLabels[kind])
}

export type BookingFilterIcon = ReturnType<typeof bookingStatusBadgeIcon> | 'todo'

export const bookingFilterStyles: Record<DashboardBookingFilter, { icon: BookingFilterIcon, color: BookingStatusBadgeColor }> = {
  TODAY: { icon: 'todo', color: 'primary' },
  BOOKING_REQUESTS: { icon: 'smPlus', color: 'primary' },
  CANCEL_REQUESTS: { icon: 'smCross', color: 'error' },
  TO_COMPLETE: { icon: 'smDoubleChevron', color: 'success' },
  RESCHEDULE_REQUESTS: { icon: 'reschedule', color: 'primary' },
  CONSULTATION_FORM_REQUIRED: { icon: 'consultationForm', color: 'warning' },
  DEPOSIT_NOT_PAID: { icon: 'smPound', color: 'warning' },
  PAYMENT_OUTSTANDING: { icon: 'smPound', color: 'warning' },
}
